/**
 * Base fields every inode carries, regardless of schema version. Not exported —
 * consumers always receive the `PlayFileInode` union and narrow with
 * `hasStats` to access V2-only fields.
 */
interface PlayFileInodeBase {
  /**
   * Where to find this file. The FS uses this to figure out which driver to initialize.
   * The convention is "<kind>:<opaque locator>". The kind field picks the driver and the
   * locator is used by the driver to actually manipulate the file.
   */
  uri: string;
  /** MIME type determining content semantics and media type handling */
  mimeType: string;
  creator: string;
  /** Currently unused */
  permissions: string;
  /**
   * When set, this inode is part of a read-only mount — the mount root and every
   * entry materialized under it. Writes through the FS to a `readonly` inode (or
   * into a `readonly` directory) must be rejected: managed content is published
   * out-of-band, never mutated in place. Absent ⇒ writable, the normal case.
   */
  readonly?: boolean;
  /**
   * Exact content this inode reads at, when pinned. Absent on a live inode,
   * which reads its `uri` live. Set on every in-scope file inode of a
   * checkpoint's index clone (`history/capture.ts`); `fs/open.ts` honors it
   * wherever it appears, so a versioned inode never falls back to live bytes.
   */
  version?: FileVersion;
}

/**
 * A pinned file version. `automerge` names a doc at `heads` (URL-encoded, as
 * `DocHandle.heads()` returns them); `blob` names an immutable object the
 * configured `BlobStore` can read by `blobId`. Never carries credentials.
 */
export type FileVersion =
  | { kind: "automerge"; documentId: string; heads: string[] }
  | { kind: "blob"; blobId: string };

/**
 * The original inode shape. Read-only from this codebase's perspective — there
 * is no longer any writer that produces a V1 inode; the union exists so readers
 * can still handle the long-tail of pre-migration data in existing projects.
 */
export type PlayFileInodeV1 = PlayFileInodeBase;

/**
 * The current inode shape. Every writer must produce one of these — the
 * compiler refuses an inode missing `mtime`/`size`. Readers narrow via
 * {@link hasStats}; everything else (V1 + V2) reads cleanly through the union.
 */
export interface PlayFileInodeV2 extends PlayFileInodeBase {
  /**
   * Epoch ms of the last body or entries change. Bumped on:
   * - file body writes (text/blob/doc)
   * - directory mutations (add / remove / rename of an entry)
   *
   * Description-only updates do NOT bump mtime.
   */
  mtime: number;
  /**
   * Size of the file's body in bytes. For text files: UTF-8 encoded byte
   * length. For blobs: the stored byte count. For directories: 0 (matches
   * the bash adapter's existing `dirStat()`).
   */
  size: number;
}

export type PlayFileInode = PlayFileInodeV1 | PlayFileInodeV2;

/**
 * Narrow a `PlayFileInode` to V2 (mtime/size present). Readers that want a
 * fast path should branch on this and fall back to opening the file body only
 * for the V1 long-tail.
 */
export function hasStats(inode: PlayFileInode): inode is PlayFileInodeV2 {
  return (
    "mtime" in inode &&
    typeof inode.mtime === "number" &&
    "size" in inode &&
    typeof inode.size === "number"
  );
}

/** A directory whose entries are inline in its parent's document (the
 *  original tree), or a read-only mount root. */
export type PlayDirInode = PlayFileInode & { files: PlayFileList };

/**
 * A directory stored as its own Automerge document (`DirectoryDoc`). The
 * parent's entry links it by `uri`; its entries live only in that document, so
 * a rename or move rewrites one entry and never copies the subtree. A link is a
 * reference, not an access grant: reading through it needs access to the
 * linked document itself.
 */
export type LinkedDirInode = PlayFileInodeV2 & {
  mimeType: "inode/directory";
  files?: never;
};

/** Any directory: inline, mount, or linked. */
export type DirInode = PlayDirInode | LinkedDirInode;

/** The document behind a {@link LinkedDirInode}: its immediate entries only. */
export interface DirectoryDoc {
  entries: Record<string, PlayFileInode>;
}

export type PlayFileList = {
  schemaVersion: 2026021200;
  entries: Record<string, PlayFileInode>;
};

/** JSON Patch operation per RFC 6902 */
import type { Patch } from "@automerge/automerge";
import type { Operation } from "fast-json-patch";

import type { AnnotatedDoc } from "../annotations/annotations";
import type { RecordsFile } from "../records/records";
export type JsonPatchOp = Operation;

/**
 * Opts every mutating method carries. `message` is propagated to the
 * underlying storage's change-attribution (Automerge's `change` message)
 * and to the inode-stamp project-doc commit, so the same author tag flows
 * through both writes.
 */
export interface ChangeOpts {
  message?: string;
}

/**
 * The mutable view editors get inside a {@link CrdtPlayFile.change}
 * callback for text files. Holds the `text` field and any other fields the
 * underlying doc may carry. Editors mutate `text` (typically via
 * `Automerge.splice`); other fields are passed through untouched.
 */
export interface TextDoc extends AnnotatedDoc {
  text?: string;
}

/**
 * Patches emitted by `onChange`. Same shape as Automerge's native change
 * event — passed through unmodified.
 */
export type TextPatch = Patch;

/**
 * One `onChange` delivery, local or remote: the patches plus the content they
 * produced, so a subscriber takes its state from the event and never races a
 * follow-up `read()`.
 */
export interface ChangeEvent<TContent> {
  patches: TextPatch[];
  content: TContent;
}

/**
 * The base contract every file kind implements. Read content, replace
 * content, get size/mtime, remove, close. The content type `TContent`
 * varies per kind (string for text, T for structured, Uint8Array for
 * blob) — that's intrinsic, not an accident of evolution.
 *
 * Capability-specific operations are layered on as traits ({@link Crdt},
 * {@link Streamable}) and intersected into the per-kind type aliases
 * below. Composition rather than inheritance: a future streaming-CRDT
 * file would just be `PlayFile<X> & Crdt<…> & Streamable` — neither
 * trait knows or cares about the other.
 */
export interface PlayFile<TContent> {
  kind: string;
  /** Read the file's full content. */
  read(): Promise<TContent>;
  /** Replace the file's content. The inode is stamped on completion. */
  write(content: TContent, opts?: ChangeOpts): Promise<void>;
  /** Size of the content in bytes. */
  size(): Promise<number>;
  /** Last modification time. */
  mtime(): Promise<Date>;
  /** Delete the underlying body. The inode is removed by {@link Playground.deleteFile}. */
  remove(): Promise<void>;
  /** Release any held resources (handles, subscriptions). */
  close(): Promise<void>;
}

/**
 * Trait: CRDT-flavoured operations on top of a {@link PlayFile}.
 *
 * `patch(p)` applies an incremental change description; the shape varies
 * per kind so `TPatch` is a parameter (search/replace blocks for text,
 * JSON Patch for structured).
 *
 * `change(fn)` and `onChange(listener)` are editor-grade hooks: granular
 * splices and remote-sync subscription. `TChangeDoc` is the raw doc shape
 * exposed to the `change` callback — may be wider than `TContent`
 * (e.g. text's content is `string` but the doc is `{text?: string}` so
 * editors can splice `["text"]` directly).
 *
 * Blobs don't compose this trait because the blob store has no
 * concurrent-merge semantics or live sync — once written, a blob just
 * sits there.
 */
export interface Crdt<TContent, TPatch, TChangeDoc = TContent> {
  patch(p: TPatch, opts?: ChangeOpts): Promise<TContent>;
  change(fn: (doc: TChangeDoc) => void, opts?: ChangeOpts): Promise<void>;
  onChange(listener: (e: ChangeEvent<TContent>) => void): () => void;
}

/** A single ephemeral message received from a peer on a doc's presence channel. */
export interface EphemeralEvent<M = unknown> {
  /**
   * Opaque per-connection sender id (an Automerge `PeerId`). NOT a stable user
   * id — identity that must survive reconnects travels *inside* `message`.
   */
  senderId: string;
  /** The payload the peer broadcast. */
  message: M;
}

/**
 * Trait: transient peer awareness (cursors, selections, "who's here") carried
 * on a doc's Automerge **ephemeral-message** channel. Nothing here touches the
 * CRDT — messages are fire-and-forget and never persisted, so they're the right
 * tool for live presence layered on top of a {@link StructuredFile}.
 *
 * Both ops are generic in the message shape `M` so a consumer can pin one
 * payload type (e.g. a pixel cursor) and stay type-safe end to end; the default
 * `unknown` keeps the bare interface honest.
 */
export interface Presence {
  /** Broadcast a transient message to every peer subscribed to this doc. */
  broadcast<M = unknown>(message: M): void;
  /** Subscribe to peers' ephemeral messages on this doc; returns an unsubscribe. */
  onEphemeral<M = unknown>(
    listener: (e: EphemeralEvent<M>) => void,
  ): () => void;
}

/**
 * Trait: chunked-read support. Blobs compose this for big binary content
 * (avoid buffering an entire image/PDF in memory). Text/structured docs
 * don't compose it because Automerge's memory model already bounds their
 * size — they load whole. Could compose onto a `Crdt<…>` file in the
 * future if a streaming-CRDT use case shows up.
 */
export interface Streamable {
  stream(): ReadableStream<Uint8Array>;
}

/** Types accepted by {@link BlobFile.write}. */
export type BlobInput =
  | string
  | Uint8Array
  | ArrayBuffer
  | Blob
  | ReadableStream<Uint8Array>
  | Response;

/**
 * A text file: Automerge-backed `{text: string}` doc with the {@link Crdt}
 * trait — search/replace blocks for `patch`, splice-grade `change`. The
 * default for any `automerge:` URI.
 */
export type TextFile = PlayFile<string> &
  Crdt<string, string, TextDoc> & { kind: "text" };

/**
 * A structured file: Automerge-backed typed doc with the {@link Crdt}
 * trait — JSON Patch for `patch`, typed `change` callback — plus the
 * {@link Presence} trait for transient peer awareness (live cursors). Used for
 * non-text CRDT files — TLDraw sketches and the PixelPusher canvas are the
 * current examples; new structured docs use the `automerge/<sub>` MIME
 * convention (`automerge/tldraw`, `automerge/pixels`).
 *
 * The contract, the same on the host and in an App sandbox:
 *  - `change(fn)` runs `fn` synchronously against the live Automerge doc, so
 *    list and text operations, conflicts and `Counter`s keep their native
 *    semantics (a plain `n++` is still an assignment). It is one change to one
 *    doc, not a transaction; a throwing `fn` applies nothing.
 *  - `patch(ops)` is JSON Patch applied through that same change path, against
 *    the holder's current state.
 *  - `write(doc)` replaces the whole document and clobbers concurrent field
 *    edits. Seed a new file with `open(path, { create, initial })` instead.
 *  - Reads see the holder's own edits at once and peers' edits once synced.
 *  - A mutation resolves once the host's replica holds it. Durable persistence
 *    is the host Repo's storage and sync, which it does not wait for.
 */
export type StructuredFile<
  T extends Record<string, unknown> = Record<string, unknown>,
> = PlayFile<T> & Crdt<T, JsonPatchOp[]> & Presence & { kind: "structured" };

/**
 * A blob file: opaque binary content backed by a {@link BlobStore},
 * composed with the {@link Streamable} trait. No CRDT capabilities.
 *
 * `write` accepts the wider {@link BlobInput} union (string, ArrayBuffer,
 * Blob, ReadableStream, Response) rather than just `Uint8Array`. The
 * content type as exposed by `read()` is `Uint8Array`.
 */
export type BlobFile = Omit<PlayFile<Uint8Array>, "write"> &
  Streamable & {
    kind: "blob";
    write(data: BlobInput, opts?: ChangeOpts): Promise<void>;
  };

/**
 * The discriminated union over all file kinds. Consumers of
 * {@link Playground.open} get this back and narrow on `kind`. A
 * {@link RecordsFile} is a structured file with record operations on top,
 * chosen by its `automerge/records` MIME.
 */
export type AnyPlayFile = TextFile | StructuredFile | RecordsFile | BlobFile;
